import { useCallback, useEffect, useRef, useState } from 'react';
import { LiveMatchStateInput } from 'api/graphql-stream-global-types';
import { GetStreamBySlug_getStreamBySlug_overlayConfig_liveMatchState } from 'api/agora/types/GetStreamBySlug';
import { MatchupCorner } from 'api/graphql-global-types';
import { MatchupFields as Matchup } from 'api/matchup/types/MatchupFields';
import {
  getCompetitorName,
  getCompetitorByCorner,
  getRoundDurationSeconds,
} from './fightCardHelpers';
import { EMPTY_LIVE_MATCH_STATE, LiveMatchState } from './fightCardTypes';

const RUNNING_SYNC_EVERY_SECONDS = 5;

const fromPersisted = (
  persisted?: GetStreamBySlug_getStreamBySlug_overlayConfig_liveMatchState | null
): LiveMatchState => {
  if (!persisted) {
    return { ...EMPTY_LIVE_MATCH_STATE };
  }
  return {
    currentMatchupId: persisted.currentMatchupId ?? null,
    currentRound: persisted.currentRound ?? 1,
    roundTimerSeconds: persisted.roundTimerSeconds ?? 0,
    isTimerRunning: persisted.isTimerRunning ?? false,
    winnerCorner: (persisted.winnerCorner as MatchupCorner | null) ?? null,
  };
};

export type UseLiveMatchStateArgs = {
  canControl: boolean;
  matchups: Matchup[];
  persisted?: GetStreamBySlug_getStreamBySlug_overlayConfig_liveMatchState | null;
  onPersist: (next: LiveMatchStateInput | null) => void;
};

export const useLiveMatchState = ({
  canControl,
  matchups,
  persisted,
  onPersist,
}: UseLiveMatchStateArgs) => {
  const [state, setState] = useState<LiveMatchState>(() =>
    fromPersisted(persisted)
  );
  const [isHidden, setIsHidden] = useState(false);
  const isHiddenRef = useRef(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current || persisted === undefined) {
      return;
    }
    hydratedRef.current = true;
    if (persisted) {
      setState(fromPersisted(persisted));
    }
  }, [persisted]);

  const matchupsRef = useRef(matchups);
  matchupsRef.current = matchups;
  const onPersistRef = useRef(onPersist);
  onPersistRef.current = onPersist;
  const stateRef = useRef(state);
  stateRef.current = state;

  const toInput = useCallback(
    (next: LiveMatchState): LiveMatchStateInput | null => {
      if (!next.currentMatchupId) {
        return null;
      }
      const matchup = matchupsRef.current.find(
        (m) => m.id === next.currentMatchupId
      );
      return {
        currentMatchupId: next.currentMatchupId,
        currentRound: next.currentRound,
        roundTimerSeconds: next.roundTimerSeconds,
        isTimerRunning: next.isTimerRunning,
        winnerCorner: next.winnerCorner,
        fighterA: matchup
          ? getCompetitorName(getCompetitorByCorner(matchup, MatchupCorner.A))
          : null,
        fighterB: matchup
          ? getCompetitorName(getCompetitorByCorner(matchup, MatchupCorner.B))
          : null,
        weightClass: matchup?.category ?? null,
        rounds: matchup?.rounds ?? null,
        roundDurationSeconds: matchup ? getRoundDurationSeconds(matchup) : null,
      };
    },
    []
  );

  const persistState = useCallback(
    (next: LiveMatchState) => {
      onPersistRef.current(isHiddenRef.current ? null : toInput(next));
    },
    [toInput]
  );
  const persistStateRef = useRef(persistState);
  persistStateRef.current = persistState;

  const commit = useCallback(
    (updater: (prev: LiveMatchState) => LiveMatchState, persistNow = true) => {
      if (!canControl) {
        return;
      }
      const next = updater(stateRef.current);
      stateRef.current = next;
      setState(next);
      if (persistNow) {
        persistStateRef.current(next);
      }
    },
    [canControl]
  );

  useEffect(() => {
    if (!state.isTimerRunning) {
      return;
    }
    const interval = setInterval(() => {
      const prev = stateRef.current;
      if (!prev.isTimerRunning) {
        return;
      }
      const duration = getRoundDurationSeconds(
        matchupsRef.current.find((m) => m.id === prev.currentMatchupId)
      );
      const nextSeconds = prev.roundTimerSeconds + 1;
      const next =
        nextSeconds >= duration
          ? { ...prev, roundTimerSeconds: duration, isTimerRunning: false }
          : { ...prev, roundTimerSeconds: nextSeconds };
      stateRef.current = next;
      setState(next);
      if (
        !next.isTimerRunning ||
        nextSeconds % RUNNING_SYNC_EVERY_SECONDS === 0
      ) {
        persistStateRef.current(next);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [state.isTimerRunning]);

  const setHidden = useCallback(
    (hidden: boolean) => {
      if (!canControl) {
        return;
      }
      isHiddenRef.current = hidden;
      setIsHidden(hidden);
      onPersistRef.current(hidden ? null : toInput(stateRef.current));
    },
    [canControl, toInput]
  );

  const unhide = () => {
    isHiddenRef.current = false;
    setIsHidden(false);
  };

  const clearFight = useCallback(() => {
    unhide();
    commit(() => ({ ...EMPTY_LIVE_MATCH_STATE, currentMatchupId: null }));
  }, [commit]);

  const startRound = useCallback(
    (matchupId: string) => {
      unhide();
      commit((prev) => {
        if (prev.currentMatchupId !== matchupId) {
          return {
            ...EMPTY_LIVE_MATCH_STATE,
            currentMatchupId: matchupId,
            isTimerRunning: true,
          };
        }
        const matchup = matchupsRef.current.find((m) => m.id === matchupId);
        const roundOver =
          prev.roundTimerSeconds >= getRoundDurationSeconds(matchup);
        if (roundOver) {
          const totalRounds = matchup?.rounds ?? null;
          if (totalRounds && prev.currentRound >= totalRounds) {
            return prev;
          }
          return {
            ...prev,
            currentRound: prev.currentRound + 1,
            roundTimerSeconds: 0,
            isTimerRunning: true,
          };
        }
        return { ...prev, isTimerRunning: true };
      });
    },
    [commit]
  );

  const pauseTimer = useCallback(
    () => commit((prev) => ({ ...prev, isTimerRunning: false })),
    [commit]
  );

  const resetTimer = useCallback(
    () =>
      commit((prev) => ({
        ...prev,
        roundTimerSeconds: 0,
        isTimerRunning: false,
      })),
    [commit]
  );

  const setRound = useCallback(
    (round: number) =>
      commit((prev) => ({
        ...prev,
        currentRound: Math.max(1, round),
        roundTimerSeconds: 0,
        isTimerRunning: false,
      })),
    [commit]
  );

  const nextRound = useCallback(
    () =>
      commit((prev) => ({
        ...prev,
        currentRound: prev.currentRound + 1,
        roundTimerSeconds: 0,
        isTimerRunning: false,
      })),
    [commit]
  );

  const prevRound = useCallback(
    () =>
      commit((prev) => ({
        ...prev,
        currentRound: Math.max(1, prev.currentRound - 1),
        roundTimerSeconds: 0,
        isTimerRunning: false,
      })),
    [commit]
  );

  const declareWinner = useCallback(
    (corner: MatchupCorner | null) =>
      commit((prev) => ({
        ...prev,
        winnerCorner: corner,
        isTimerRunning: corner ? false : prev.isTimerRunning,
      })),
    [commit]
  );

  return {
    state,
    isHidden,
    setHidden,
    clearFight,
    startRound,
    pauseTimer,
    resetTimer,
    setRound,
    nextRound,
    prevRound,
    declareWinner,
  };
};
